import { css } from '@emotion/react';
import {
  Box,
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ClockCounterClockwiseIcon,
  ExportIcon,
  FloppyDiskIcon,
  MapPinIcon,
  PencilIcon,
  TrashSimpleIcon,
} from '@phosphor-icons/react';
import type { LinkOptions } from '@tanstack/react-router';
import { useRouterState } from '@tanstack/react-router';
import type { MouseEvent } from 'react';
import { useState } from 'react';

import MenuFilledIcon from '@/assets/icons/menu.svg?react';
import { BreadcrumbsNav } from '@/components/management/BreadcrumbsNav';
import { ButtonLink } from '@/components/router/ButtonLink';
import { MenuItemLink } from '@/components/router/MenuItemLink';
import { useAbility } from '@/hooks/useAbility';
import { MAIN_BREAKPOINT } from '@/modules/MAIN_BREAKPOINT';
import { SCREEN_HEIGHT_BREAKPOINT } from '@/modules/SCREEN_HEIGHT_BREAKPOINT';
import { Action, Subject } from '@/modules/api/backend';
import { IconTyp } from '@/types/IconType';
import { buildExportUrl } from '@/utils/buildExportUrl';

const Root = styled(Box)(
  ({ theme }) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    background: ${theme.palette.secondary.light};
    padding: 0 ${theme.spacing(3)};
    height: ${theme.spacing(15)};

    ${theme.breakpoints.up(MAIN_BREAKPOINT)} {
      padding: 0 ${theme.spacing(13.125)};
    }

    @media (min-height: ${SCREEN_HEIGHT_BREAKPOINT}) and (min-width: ${theme.breakpoints.values[MAIN_BREAKPOINT]}px) {
      position: sticky;
      top: ${theme.spacing(8)};
      z-index: 1200;
    }
  `,
);

const DesktopButtons = styled(Stack)(
  ({ theme }) => css`
    display: none;

    ${theme.breakpoints.up(MAIN_BREAKPOINT)} {
      display: flex;
      flex-direction: row;
      gap: ${theme.spacing(3)};
    }
  `,
);

const MobileButtons = styled(Stack)(
  ({ theme }) => css`
    display: inherit;

    ${theme.breakpoints.up(MAIN_BREAKPOINT)} {
      display: none;
    }
  `,
);

const ICON = {
  [IconTyp.PENCIL]: <PencilIcon size={20} weight="regular" />,
  [IconTyp.CLOCK]: <ClockCounterClockwiseIcon size={20} weight="regular" />,
  [IconTyp.MAP_PIN]: <MapPinIcon size={20} weight="regular" />,
};

interface AdministrationHeaderProps {
  label: string;
  canExport?: boolean;
  create?: boolean;
  formId?: string;
  links?: { name: string; options: LinkOptions; icon?: IconTyp }[];
  onDelete?: () => void;
  search?: string;
  organizationId?: string;
}

export function AdministrationHeader({
  label,
  canExport,
  create,
  formId,
  links,
  onDelete,
  search,
  organizationId,
}: AdministrationHeaderProps) {
  const theme = useTheme();
  const { ability } = useAbility();

  const matches = useRouterState({ select: (s) => s.matches });
  const breadcrumbs = matches
    .map(({ pathname, context }) => ({
      title: context.breadcrumb,
      pathname,
    }))
    .filter((breadcrumb): breadcrumb is { title: string; pathname: string } => !!breadcrumb.title);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleOpen = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const currentPathname = matches[matches.length - 1]?.pathname ?? '';

  const exportHref = buildExportUrl({
    search,
    organizationId: organizationId ?? undefined,
    type: currentPathname.includes('/stationless') ? 'parking-facilities' : 'park-and-ride-facilities',
  });

  return (
    <Root>
      <Stack>
        <BreadcrumbsNav breadcrumbs={breadcrumbs} />
        <Typography color="primary" variant="h1">
          {label}
        </Typography>
      </Stack>

      <DesktopButtons>
        {links
          ? links.map(({ name, options }) => (
              <ButtonLink key={name} color="primary" variant="outlined" {...options}>
                {name}
              </ButtonLink>
            ))
          : null}

        {canExport ? (
          <Button download component="a" href={exportHref} variant="outlined">
            P+R Anlagen exportieren
          </Button>
        ) : null}

        {create || formId ? (
          <Button type="submit" variant="contained" {...(formId ? { form: formId } : {})}>
            Speichern
          </Button>
        ) : null}

        {onDelete && ability?.can(Action.DELETE, Subject.ParkAndRideFacility) ? (
          <IconButton onClick={onDelete}>
            <TrashSimpleIcon color={theme.palette.error.main} size={24} weight="fill" />
          </IconButton>
        ) : null}
      </DesktopButtons>

      {links || canExport || create || formId || onDelete ? (
        <MobileButtons>
          <IconButton
            onClick={handleOpen}
            sx={{
              backgroundColor: 'primary.main',
              color: 'common.white',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            <MenuFilledIcon style={{ fill: 'currentColor' }} />
          </IconButton>

          <Menu anchorEl={anchorEl} onClose={handleMenuClose} open={menuOpen}>
            {links
              ? links.map(({ name, options, icon }) => (
                  <MenuItemLink key={name} {...options} onClick={handleMenuClose}>
                    {icon ? <ListItemIcon>{ICON[icon]}</ListItemIcon> : null}
                    <ListItemText>{name}</ListItemText>
                  </MenuItemLink>
                ))
              : null}

            {canExport ? (
              <MenuItem download component="a" href={exportHref} onClick={handleMenuClose}>
                <ListItemIcon>
                  <ExportIcon size={20} />
                </ListItemIcon>
                <ListItemText>P+R Anlagen exportieren</ListItemText>
              </MenuItem>
            ) : null}

            {create || formId ? (
              <MenuItem component="button" form={formId} onClick={handleMenuClose} type="submit">
                <ListItemIcon>
                  <FloppyDiskIcon size={20} weight="regular" />
                </ListItemIcon>
                <ListItemText>Speichern</ListItemText>
              </MenuItem>
            ) : null}

            {onDelete && ability?.can(Action.DELETE, Subject.ParkAndRideFacility) ? (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  onDelete();
                }}
              >
                <ListItemIcon>
                  <TrashSimpleIcon color={theme.palette.error.main} size={20} weight="fill" />
                </ListItemIcon>
                <ListItemText>Löschen</ListItemText>
              </MenuItem>
            ) : null}
          </Menu>
        </MobileButtons>
      ) : null}
    </Root>
  );
}
